/** ContactAddresses — list + add/edit/delete addresses on a Contact.
 *
 * Renders the multi-line `formatted` string per address (the backend
 * computes it from each country's catalog) and exposes the
 * country-aware AddressForm for create + edit.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import type { Address } from "@/lib/types";

import { AddressForm } from "./AddressForm";
import { addressPurposeLabelKey } from "./addressLabels";
import { deleteContactAddress } from "./api";


/** Is this address actually missing structure?
 *
 *  `legacy_unparsed` alone is the wrong question. The importer sets it
 *  on EVERY site it creates, so the badge was on 10,741 of 10,743
 *  addresses — 100% of worksites — and a badge that is on everything
 *  tells you nothing. 8,201 of those had a locality parsed out; they
 *  were flagged only because the importer flags unconditionally.
 *
 *  What the badge is meant to say is "this address is one unsplit blob
 *  of text, go and separate it". That is true when the row carries no
 *  locality and no postcode.
 *
 *  Deliberately NOT fixed by writing `legacy_unparsed=false` at import
 *  time: that flag does double duty in campaigns/signals.py, where
 *  `not primary.legacy_unparsed` means "an operator structured this by
 *  hand, never clobber it". Clearing it for well-formed imports would
 *  silently stop later imports refreshing a contact's address — a much
 *  bigger change than a badge, and not the one that was asked for.
 */
function needsReview(addr: Address): boolean {
  if (!addr.legacy_unparsed) return false;
  return !((addr.locality || "").trim() || (addr.postal_code || "").trim());
}

export function ContactAddresses({
  contactId,
  isBusiness,
}: {
  contactId: string;
  /** When the Contact is a legal entity, its main (`beneficiary`) address is
   *  labelled as the company's registered office. */
  isBusiness?: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Address | undefined>(undefined);
  const [adding, setAdding] = useState(false);

  const list = useQuery({
    queryKey: ["contact-addresses", contactId],
    queryFn: async () => {
      const res = await api.get<Address[]>(
        ApiPaths.contactAddresses(contactId),
      );
      return res.data;
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => deleteContactAddress(contactId, id),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["contact-addresses", contactId],
      });
      void qc.invalidateQueries({ queryKey: ["contact", contactId] });
    },
  });

  if (list.isLoading) {
    return <p className="text-xs text-nb-text-3">{t("common.loading")}</p>;
  }

  const rows = list.data ?? [];

  return (
    <section className="card space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {isBusiness
            ? t("contacts.addressHeadingCompany", "Company address")
            : t("contacts.addressesHeading", "Addresses")}
        </h2>
        {!adding && !editing && (
          <button
            type="button"
            className="btn-primary text-xs"
            onClick={() => setAdding(true)}
          >
            {t("contacts.addresses.addButton", "Add address")}
          </button>
        )}
      </div>

      {adding && (
        <AddressForm
          contactId={contactId}
          isBusiness={isBusiness}
          onSaved={() => setAdding(false)}
          onCancel={() => setAdding(false)}
        />
      )}

      {editing && (
        <AddressForm
          contactId={contactId}
          address={editing}
          isBusiness={isBusiness}
          onSaved={() => setEditing(undefined)}
          onCancel={() => setEditing(undefined)}
        />
      )}

      {rows.length === 0 && !adding && (
        <p className="text-xs text-nb-text-3">
          {t("contacts.addresses.empty", "No addresses yet.")}
        </p>
      )}

      <ul className="divide-y divide-nb-border">
        {rows.map((addr) => (
          <li key={addr.id} className="flex items-start justify-between py-3 text-sm">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <span className="rounded bg-nb-surface px-1.5 py-0.5 text-[10px] uppercase text-nb-text-2">
                  {t(
                    addressPurposeLabelKey(addr.purpose, isBusiness),
                    addr.purpose,
                  )}
                </span>
                {addr.is_primary && (
                  <span className="rounded bg-nb-mint/20 px-1.5 py-0.5 text-[10px] uppercase text-nb-mint">
                    {t("contacts.addresses.primaryBadge", "primary")}
                  </span>
                )}
                {addr.label && (
                  <span className="text-xs text-nb-text-2">{addr.label}</span>
                )}
                {needsReview(addr) && (
                  <span
                    className="rounded bg-cvp-warn/20 px-1.5 py-0.5 text-[10px] uppercase text-cvp-warn"
                    title={t("contacts.addresses.needsReviewHint")}
                  >
                    {t(
                      "contacts.addresses.needsReview",
                      "needs review",
                    )}
                  </span>
                )}
              </div>
              <pre className="font-sans text-sm whitespace-pre-wrap text-nb-text">
                {addr.formatted}
              </pre>
              {Object.entries(addr.custom_fields_jsonb || {}).length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1 text-[10px]">
                  {Object.entries(addr.custom_fields_jsonb).map(([k, v]) => (
                    <span
                      key={k}
                      className="rounded bg-nb-surface px-1.5 py-0.5 font-mono text-nb-text-2"
                    >
                      {k}: {String(v)}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <button
                type="button"
                className="text-xs text-nb-text-2 hover:text-nb-mint"
                onClick={() => setEditing(addr)}
              >
                {t("common.edit", "Edit")}
              </button>
              <button
                type="button"
                className="text-xs text-cvp-danger hover:underline"
                onClick={() => {
                  if (
                    window.confirm(
                      t(
                        "contacts.addresses.deleteConfirm",
                        "Delete this address?",
                      ),
                    )
                  ) {
                    del.mutate(addr.id);
                  }
                }}
              >
                {t("common.delete", "Delete")}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
